import { Title } from '@solidjs/meta'
import { type Component, createEffect, createMemo, createResource, createSignal, For, onCleanup, onMount, Show } from 'solid-js'
import Avatar from '~/components/Avatar'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Menu from '~/components/Menu'
import Select from '~/components/Select'
import Skeleton from '~/components/Skeleton'
import Switch from '~/components/Switch'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { usePermissions } from '~/hooks/usePermissions'
import CrmCustomerDrawer from '~/layers/CrmCustomerDrawer'
import { KANBAN_ROW_HEIGHT, KanbanColumn } from '~/layers/KanbanColumn'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { type Path, useNavigate } from '~/router'
import { type BoardCustomerType, type BoardLeadType, customerService } from '~/services/customer.service'
import { leadService } from '~/services/lead.service'
import { resolveError } from '~/utils/error'
import { kanbanColumnStyle } from '~/utils/kanban'
import { P } from '~/utils/permissions'
import { classificationOptions, customerStateColors, formatMoney } from '~/utils/settings'

/** Etapa que recoge a los clientes cuyo estado ya no existe en la configuración. */
const SIN_ETAPA = '__sin_etapa__'

/**
 * Lo que se arrastra. Viaja serializado en `text/plain`, igual que en los
 * constructores de páginas y formularios: es el único formato que todos los
 * navegadores respetan en el portapapeles de arrastre.
 */
type DragPayloadType = { kind: 'customer'; id: number } | { kind: 'lead'; id: number }

const readPayload = (e: DragEvent): DragPayloadType | null => {
  try {
    const raw = e.dataTransfer?.getData('text/plain')
    return raw ? (JSON.parse(raw) as DragPayloadType) : null
  } catch {
    return null
  }
}

const CrmBoardPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { settings } = useCompanySettings()

  const [board, { refetch, mutate }] = createResource(async () => {
    try {
      return await customerService.board()
    } catch {
      return { customers: [] as BoardCustomerType[], leads: [] as BoardLeadType[] }
    }
  })

  /**
   * Alto real disponible para el tablero, medido en vivo.
   *
   * Con un `calc(100dvh - Xrem)` había que adivinar cuánto ocupaban la cabecera
   * del panel, la de la página y el pie, y el número cambiaba en cada tamaño de
   * pantalla: sobraba hueco abajo en el móvil y faltaba en el escritorio. Aquí
   * se mide desde dónde empieza el tablero hasta el final de la ventana.
   */
  const [altoTablero, setAltoTablero] = createSignal(0)
  let tableroRef: HTMLDivElement | undefined

  const medirTablero = () => {
    if (!tableroRef) return
    const arriba = tableroRef.getBoundingClientRect().top
    // 96px: el pie con la leyenda y el total, más el respiro inferior del panel.
    setAltoTablero(Math.max(240, window.innerHeight - arriba - 96))
  }

  onMount(() => {
    medirTablero()
    window.addEventListener('resize', medirTablero)
    onCleanup(() => window.removeEventListener('resize', medirTablero))
  })

  // Los chips de filtros activos y el menú plegable mueven el tablero hacia
  // abajo: hay que volver a medir o quedaría un hueco (o se saldría de pantalla).
  createEffect(() => {
    void chipsFiltros().length
    void board.loading
    queueMicrotask(medirTablero)
  })

  const [dragging, setDragging] = createSignal(false)
  const [columnaActiva, setColumnaActiva] = createSignal<string | null>(null)
  const [detalleId, setDetalleId] = createSignal<number | null>(null)

  // ── Filtros ────────────────────────────────────────────────────────────────
  // Se aplican en memoria: el tablero ya tiene todas las tarjetas cargadas, así
  // que filtrar no necesita ir al servidor y responde al instante.
  const [filtrosAbiertos, setFiltrosAbiertos] = createSignal(false)
  const [busqueda, setBusqueda] = createSignal('')
  const [filtroClasificacion, setFiltroClasificacion] = createSignal('')
  const [filtroPeriodo, setFiltroPeriodo] = createSignal('')
  const [soloConImporte, setSoloConImporte] = createSignal(false)

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroClasificacion('')
    setFiltroPeriodo('')
    setSoloConImporte(false)
  }

  const filtrosActivos = () => [busqueda().trim(), filtroClasificacion(), filtroPeriodo(), soloConImporte() ? 'x' : ''].filter(Boolean).length

  /** Desde cuándo cuenta el periodo elegido. */
  const desdeFecha = () => {
    const ahora = new Date()
    if (filtroPeriodo() === 'mes') return new Date(ahora.getFullYear(), ahora.getMonth(), 1)
    if (filtroPeriodo() === 'trimestre') return new Date(ahora.getFullYear(), ahora.getMonth() - 3, 1)
    if (filtroPeriodo() === 'anio') return new Date(ahora.getFullYear(), 0, 1)
    return null
  }

  const coincide = (c: BoardCustomerType) => {
    const texto = busqueda().trim().toLowerCase()
    if (texto) {
      const campos = [c.name, c.razonSocial, c.nombreComercial, c.email, c.phone, c.celular]
      if (!campos.some(v => (v ?? '').toLowerCase().includes(texto))) return false
    }
    if (filtroClasificacion() && c.clasificacion !== filtroClasificacion()) return false
    if (soloConImporte() && !(c.amount > 0)) return false
    const desde = desdeFecha()
    if (desde && new Date(c.createdAt) < desde) return false
    return true
  }

  const chipsFiltros = () => {
    const chips: { label: string; onRemove: () => void }[] = []
    if (busqueda().trim()) chips.push({ label: `Texto: ${busqueda().trim()}`, onRemove: () => setBusqueda('') })
    if (filtroClasificacion()) chips.push({ label: `Clasificación: ${filtroClasificacion()}`, onRemove: () => setFiltroClasificacion('') })
    if (filtroPeriodo()) {
      const etiquetas: Record<string, string> = { mes: 'Este mes', trimestre: 'Último trimestre', anio: 'Este año' }
      chips.push({ label: etiquetas[filtroPeriodo()] ?? filtroPeriodo(), onRemove: () => setFiltroPeriodo('') })
    }
    if (soloConImporte()) chips.push({ label: 'Sólo con importe', onRemove: () => setSoloConImporte(false) })
    return chips
  }

  const money = (value: number) => formatMoney(value, settings()?.currency ?? 'COP', settings()?.currencies ?? [])

  /** Las columnas salen de la configuración de la empresa, ordenadas. */
  const etapas = () =>
    (settings()?.customerStates ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(s => ({ key: s.key, label: s.label, color: s.color }))

  const colores = () => customerStateColors(settings()?.customerStates ?? [])

  /**
   * Clientes agrupados por etapa. Los que tengan un estado que ya no existe en la
   * configuración caen en una columna final, para que nadie desaparezca del
   * tablero por editar las etapas.
   */
  const porEtapa = createMemo(() => {
    const claves = new Set(etapas().map(e => e.key))
    const mapa = new Map<string, BoardCustomerType[]>()
    for (const etapa of etapas()) mapa.set(etapa.key, [])
    mapa.set(SIN_ETAPA, [])
    for (const c of board()?.customers ?? []) {
      if (!coincide(c)) continue
      const clave = claves.has(c.estadoCliente) ? c.estadoCliente : SIN_ETAPA
      mapa.get(clave)?.push(c)
    }
    return mapa
  })

  const huerfanos = () => porEtapa().get(SIN_ETAPA) ?? []

  /** Los prospectos también responden a la búsqueda por texto. */
  const prospectos = () => {
    const texto = busqueda().trim().toLowerCase()
    const lista = board()?.leads ?? []
    if (!texto) return lista
    return lista.filter(l => [l.name, l.email, l.phone, l.interest].some(v => (v ?? '').toLowerCase().includes(texto)))
  }

  const totalColumna = (clave: string) => (porEtapa().get(clave) ?? []).reduce((suma, c) => suma + (c.amount ?? 0), 0)

  /** Suma de lo que se está viendo: con filtros activos debe cuadrar con las columnas. */
  const totalEmbudo = () => (board()?.customers ?? []).reduce((suma, c) => (coincide(c) ? suma + (c.amount ?? 0) : suma), 0)

  /** Cuántas tarjetas quedan visibles, para avisar si el filtro no deja ninguna. */
  const totalVisibles = () => etapas().reduce((n, e) => n + (porEtapa().get(e.key) ?? []).length, 0) + huerfanos().length

  /** Mueve un cliente de etapa. El backend registra la actividad por su cuenta. */
  const moverCliente = async (id: number, etapa: string) => {
    const antes = board()
    // Optimista: la tarjeta salta de columna al instante y se revierte si falla.
    mutate(prev => (prev ? { ...prev, customers: prev.customers.map(c => (c.id === id ? { ...c, estadoCliente: etapa } : c)) } : prev))
    try {
      await customerService.update(id, { estadoCliente: etapa })
    } catch (err: unknown) {
      mutate(antes)
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  /**
   * Un prospecto que sale de la primera columna se convierte en cliente.
   *
   * Se pregunta antes porque no tiene vuelta atrás desde la interfaz: crea una
   * ficha de cliente y marca el prospecto para siempre. Un roce al arrastrar o
   * un clic en el menú no deberían dar de alta un cliente.
   */
  const convertirProspecto = async (id: number, etapa: string) => {
    const lead = board()?.leads.find(l => l.id === id)
    const ok = await alert.confirm({
      title: 'Convertir en cliente',
      message: `Se creará la ficha de cliente de "${lead?.name ?? 'este prospecto'}". Esta acción no se puede deshacer.`,
      confirmText: 'Sí, convertir'
    })
    if (!ok) return
    try {
      await leadService.convert(id, etapa)
      toast.addToast({ title: 'Prospecto convertido en cliente', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const soltarEn = (e: DragEvent, etapa: string) => {
    // Sin stopPropagation el drop llega también al contenedor y se procesa dos veces.
    e.preventDefault()
    e.stopPropagation()
    setDragging(false)
    setColumnaActiva(null)
    if (etapa === SIN_ETAPA) return

    const payload = readPayload(e)
    if (!payload) return
    if (payload.kind === 'lead') {
      void convertirProspecto(payload.id, etapa)
      return
    }
    const actual = board()?.customers.find(c => c.id === payload.id)
    if (actual && actual.estadoCliente !== etapa) void moverCliente(payload.id, etapa)
  }

  const empezarArrastre = (e: DragEvent, payload: DragPayloadType) => {
    e.dataTransfer?.setData('text/plain', JSON.stringify(payload))
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
    setDragging(true)
  }

  /** Alternativa al arrastre: en móvil y con teclado no hay drag&drop. */
  const opcionesMover = (payload: DragPayloadType, etapaActual?: string) =>
    etapas()
      .filter(e => e.key !== etapaActual)
      .map(e => ({
        label: e.label,
        icon: 'material-symbols-light:arrow-forward',
        onClick: () => (payload.kind === 'lead' ? convertirProspecto(payload.id, e.key) : moverCliente(payload.id, e.key))
      }))

  /** Posición de una etapa dentro del embudo, para saber cuál es la siguiente. */
  const indiceEtapa = (clave: string) => etapas().findIndex(e => e.key === clave)

  const etapaVecina = (clave: string, direccion: 1 | -1) => {
    const i = indiceEtapa(clave)
    if (i < 0) return null
    return etapas()[i + direccion] ?? null
  }

  /** Avanzar o retroceder una etapa: es el movimiento habitual del embudo. */
  const moverVecina = (c: BoardCustomerType, direccion: 1 | -1) => {
    const destino = etapaVecina(c.estadoCliente, direccion)
    if (destino) void moverCliente(c.id, destino.key)
  }

  const empresaDe = (c: BoardCustomerType) => c.nombreComercial || c.razonSocial || ''

  /**
   * Todas las acciones de una tarjeta en un solo menú: contactar, ver la ficha y
   * cambiar de etapa. Tenerlas aquí evita la fila de iconos que no cabía en la
   * tarjeta, y es además el único camino en móvil, donde no hay arrastre.
   */
  const accionesCliente = (c: BoardCustomerType) => {
    const acciones = [
      {
        label: 'Ver detalle',
        icon: 'material-symbols-light:visibility-outline',
        onClick: () => setDetalleId(c.id)
      },
      {
        label: 'Nueva cotización',
        icon: 'material-symbols-light:request-quote-outline',
        onClick: () => navigate(`/admin/cotizaciones/nueva?cliente=${c.id}` as Path)
      },
      {
        label: 'Abrir ficha',
        icon: 'material-symbols-light:open-in-new',
        onClick: () => navigate(`/admin/clientes/${c.id}` as Path)
      }
    ]
    // Las etapas van al final, después de las acciones sobre el cliente.
    return [...acciones, ...opcionesMover({ kind: 'customer', id: c.id }, c.estadoCliente)]
  }

  return (
    <>
      <Title>CRM | Florez Jaramillo</Title>

      {/*
        La altura se descuenta por tamaño de pantalla porque la cabecera del
        tablero se apila en móvil y ocupa más: con un único `calc` el tablero se
        salía de la pantalla en el teléfono y quedaba media columna cortada.
      */}
      <div class="flex flex-col min-h-0">
        <div class="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2 class="text-lg sm:text-xl font-bold text-foreground">Gestión de Clientes</h2>
            <p class="hidden sm:block text-sm text-foreground-subtle">Visualiza y gestiona tu proceso comercial</p>
          </div>

          <div class="flex w-full sm:w-auto items-center gap-2">
            {/* Buscador siempre visible: es el filtro que más se usa. */}
            <div class="flex-1 sm:flex-none sm:w-72">
              {/* Este Input usa etiqueta flotante: el texto va en `label`, no en `placeholder`. */}
              <Input label="Buscar cliente, empresa o correo" value={busqueda()} onInput={e => setBusqueda(e.currentTarget.value)} startIcon="material-symbols-light:search" />
            </div>
            <FiltersButton activeCount={filtrosActivos()} onClick={() => setFiltrosAbiertos(true)} />
            <Show when={can(P.customers.create)}>
              {/* Sin talla: va en la misma fila que el buscador y debe medir igual. */}
              <Button color="primary" prependIcon="material-symbols-light:add" onClick={() => navigate('/admin/clientes' as Path)}>
                Nuevo cliente
              </Button>
            </Show>
          </div>
        </div>

        {/* Con filtros que no dejan nada, el tablero vacío no explica por qué. */}
        <Show when={filtrosActivos() > 0 && totalVisibles() === 0 && !board.loading}>
          <div class="mb-3 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning-soft/20 px-3 py-2 text-sm">
            <Icon icon="material-symbols-light:filter-alt-off-outline" size={18} class="text-warning shrink-0" />
            <span class="text-foreground">Ningún cliente coincide con los filtros.</span>
            <button type="button" onClick={limpiarFiltros} class="ml-auto text-primary hover:underline">
              Quitar filtros
            </button>
          </div>
        </Show>

        {/* Filtros activos a la vista, para quitarlos de uno en uno. */}
        <Show when={chipsFiltros().length > 0}>
          <div class="flex flex-wrap items-center gap-2 mb-3">
            <For each={chipsFiltros()}>
              {chip => (
                <Chip color="primary" variant="soft" size="xs" removable onRemove={chip.onRemove}>
                  {chip.label}
                </Chip>
              )}
            </For>
            <button type="button" onClick={limpiarFiltros} class="text-xs text-foreground-subtle hover:text-primary underline">
              Limpiar todo
            </button>
          </div>
        </Show>

        <Show
          when={!board.loading}
          fallback={
            <div class="flex gap-4 overflow-hidden">
              <For each={[1, 2, 3, 4, 5]}>{() => <Skeleton variant="card" class="w-72 shrink-0 h-64" />}</For>
            </div>
          }
        >
          {/*
            El tablero scrollea en horizontal y cada columna en vertical (esa la
            gestiona `VirtualList`). La barra horizontal se deja visible a
            propósito: con varias etapas es la única pista de que hay más
            columnas fuera de la pantalla.
          */}
          <div ref={tableroRef} style={{ height: `${altoTablero()}px` }} class="flex gap-3 sm:gap-4 overflow-x-auto pb-2 min-h-0 snap-x snap-mandatory sm:snap-none">
            {/* Columna de prospectos: no es una etapa, es la entrada al embudo. */}
            <Show when={prospectos().length > 0}>
              <div class="w-[85vw] shrink-0 sm:w-auto sm:min-w-60 sm:max-w-96 sm:flex-1 snap-start flex flex-col min-h-0 rounded-xl border border-dashed border-border bg-surface-muted/30">
                <div class="px-3 py-2 border-b border-border">
                  <div class="flex items-center justify-between gap-2">
                    <p class="font-semibold text-foreground text-sm">Prospectos web</p>
                    <span class="text-xs font-medium text-foreground-subtle bg-surface rounded-full px-2 py-0.5">{prospectos().length}</span>
                  </div>
                  <p class="text-xs text-foreground-subtle mt-0.5">Aún no son clientes</p>
                </div>
                <div class="flex-1 overflow-y-auto p-2 space-y-2">
                  <For each={prospectos()}>
                    {lead => (
                      <div
                        draggable={can(P.customers.create)}
                        onDragStart={e => empezarArrastre(e, { kind: 'lead', id: lead.id })}
                        onDragEnd={() => setDragging(false)}
                        class="rounded-lg border border-border bg-surface p-3 cursor-grab active:cursor-grabbing hover:border-primary-500/50 transition-colors"
                      >
                        <div class="flex items-center justify-between gap-2">
                          <div class="flex items-center gap-2 min-w-0">
                            <Avatar alt={lead.name} size={32} class="rounded-full shrink-0" />
                            <div class="min-w-0">
                              <p class="text-sm font-semibold text-foreground truncate">{lead.name}</p>
                              <p class="text-xs text-foreground-subtle truncate">{lead.interest || lead.source || 'Web'}</p>
                            </div>
                          </div>
                          <Show when={can(P.customers.create)}>
                            <Menu
                              trigger={<Button size="xs" variant="text" color="default" icon="material-symbols-light:more-vert" aria-label="Acciones" />}
                              items={opcionesMover({ kind: 'lead', id: lead.id })}
                            />
                          </Show>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            <For each={etapas()}>
              {(etapa, indice) => (
                <div
                  onDragOver={e => {
                    e.preventDefault()
                    setColumnaActiva(etapa.key)
                  }}
                  onDragLeave={() => setColumnaActiva(null)}
                  onDrop={e => soltarEn(e, etapa.key)}
                  class={`w-[85vw] shrink-0 sm:w-auto sm:min-w-60 sm:max-w-96 sm:flex-1 snap-start flex flex-col min-h-0 rounded-xl border transition-colors ${
                    columnaActiva() === etapa.key ? 'border-primary-500 bg-primary-soft/20' : 'border-border bg-surface-muted/30'
                  }`}
                >
                  {/* La cabecera lleva el color de la etapa: identifica la columna de un vistazo. */}
                  <div class={`px-3 py-2 rounded-t-xl ${kanbanColumnStyle(etapa.color).cabecera}`}>
                    <div class="flex items-center justify-between gap-2">
                      <p class="font-semibold text-foreground text-sm truncate">
                        <span class={kanbanColumnStyle(etapa.color).numero}>{indice() + 1}.</span> {etapa.label}
                      </p>
                      <span class="text-xs font-bold text-foreground bg-surface rounded-full px-2 py-0.5 shrink-0 tabular-nums">{(porEtapa().get(etapa.key) ?? []).length}</span>
                    </div>
                    <p class="text-sm font-bold text-foreground mt-0.5 tabular-nums">{money(totalColumna(etapa.key))}</p>
                  </div>
                  <div class={`h-0.5 ${kanbanColumnStyle(etapa.color).barra}`} />

                  <KanbanColumn items={porEtapa().get(etapa.key) ?? []} empty={<p class="text-xs text-foreground-subtle/70 text-center py-6">{dragging() ? 'Suelta aquí' : 'Sin clientes'}</p>}>
                    {cliente => (
                      <div
                        draggable={can(P.customers.update)}
                        onDragStart={e => empezarArrastre(e, { kind: 'customer', id: cliente.id })}
                        onDragEnd={() => setDragging(false)}
                        onClick={() => setDetalleId(cliente.id)}
                        style={{ height: `${KANBAN_ROW_HEIGHT - 8}px` }}
                        class="mb-2 overflow-hidden rounded-lg border border-border bg-surface px-2.5 py-1.5 cursor-pointer hover:border-primary-500/50 hover:shadow-sm transition-all"
                      >
                        {/*
                          Tres filas que caben justas en la altura fija de la
                          tarjeta. Antes había además una fila de iconos que se
                          salía y quedaba cortada: llamar y escribir se movieron
                          al menú, donde ya estaba "Mover a".
                        */}
                        <div class="flex items-start justify-between gap-1.5">
                          <div class="flex items-center gap-2 min-w-0">
                            <Avatar alt={cliente.name} size={28} class="rounded-full shrink-0" />
                            <div class="min-w-0">
                              <p class="text-sm font-semibold text-foreground truncate leading-tight">{cliente.name}</p>
                              <Show when={empresaDe(cliente)}>
                                <p class="text-xs text-foreground-subtle truncate leading-tight">{empresaDe(cliente)}</p>
                              </Show>
                            </div>
                          </div>
                          <Show when={can(P.customers.update)}>
                            <Menu
                              trigger={<Button size="xs" variant="text" color="default" icon="material-symbols-light:more-vert" aria-label="Acciones del cliente" />}
                              items={accionesCliente(cliente)}
                            />
                          </Show>
                        </div>

                        <div class="mt-1 flex items-center justify-between gap-2">
                          <Chip color={colores()[etapa.key] ?? 'default'} variant="soft" size="xs" class="min-w-0">
                            {etapa.label}
                          </Chip>
                          <Show when={cliente.amount > 0}>
                            <span class="text-xs font-bold text-foreground shrink-0 tabular-nums">{money(cliente.amount)}</span>
                          </Show>
                        </div>

                        {/* Acciones a la izquierda; a la derecha, avanzar o retroceder de etapa. */}
                        <div class="mt-1 flex items-center justify-between gap-1">
                          <div class="flex items-center gap-0.5 min-w-0">
                            <Show when={cliente.phone || cliente.celular}>
                              <a
                                href={`tel:${cliente.phone || cliente.celular}`}
                                onClick={e => e.stopPropagation()}
                                class="size-6 rounded flex items-center justify-center text-foreground-subtle hover:bg-surface-muted hover:text-primary"
                                aria-label="Llamar"
                              >
                                <Icon icon="material-symbols-light:call-outline" size={15} />
                              </a>
                            </Show>
                            <Show when={cliente.email}>
                              <a
                                href={`mailto:${cliente.email}`}
                                onClick={e => e.stopPropagation()}
                                class="size-6 rounded flex items-center justify-center text-foreground-subtle hover:bg-surface-muted hover:text-primary"
                                aria-label="Escribir"
                              >
                                <Icon icon="material-symbols-light:mail-outline" size={15} />
                              </a>
                            </Show>
                            <Show when={can(P.quotes.create)}>
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation()
                                  navigate(`/admin/cotizaciones/nueva?cliente=${cliente.id}` as Path)
                                }}
                                class="size-6 rounded flex items-center justify-center text-foreground-subtle hover:bg-surface-muted hover:text-primary"
                                aria-label="Nueva cotización"
                              >
                                <Icon icon="material-symbols-light:request-quote-outline" size={15} />
                              </button>
                            </Show>
                            <Show when={cliente.proximoContacto || cliente.fechaUltimoContacto}>
                              <span class="ml-1 flex items-center gap-0.5 text-[11px] text-foreground-subtle truncate">
                                <Icon icon="material-symbols-light:calendar-month-outline" size={13} />
                                {new Date(cliente.proximoContacto ?? cliente.fechaUltimoContacto ?? '').toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                              </span>
                            </Show>
                          </div>

                          <Show when={can(P.customers.update)}>
                            <div class="flex items-center gap-0.5 shrink-0">
                              <button
                                type="button"
                                disabled={!etapaVecina(cliente.estadoCliente, -1)}
                                onClick={e => {
                                  e.stopPropagation()
                                  moverVecina(cliente, -1)
                                }}
                                class="size-6 rounded flex items-center justify-center text-foreground-subtle hover:bg-surface-muted hover:text-primary disabled:opacity-25"
                                aria-label="Mover a la etapa anterior"
                                title={etapaVecina(cliente.estadoCliente, -1)?.label ?? ''}
                              >
                                <Icon icon="material-symbols-light:chevron-left" size={16} />
                              </button>
                              <button
                                type="button"
                                disabled={!etapaVecina(cliente.estadoCliente, 1)}
                                onClick={e => {
                                  e.stopPropagation()
                                  moverVecina(cliente, 1)
                                }}
                                class="size-6 rounded flex items-center justify-center text-foreground-subtle hover:bg-surface-muted hover:text-primary disabled:opacity-25"
                                aria-label="Mover a la etapa siguiente"
                                title={etapaVecina(cliente.estadoCliente, 1)?.label ?? ''}
                              >
                                <Icon icon="material-symbols-light:chevron-right" size={16} />
                              </button>
                            </div>
                          </Show>
                        </div>
                      </div>
                    )}
                  </KanbanColumn>

                  <Show when={can(P.customers.create)}>
                    <button
                      type="button"
                      onClick={() => navigate('/admin/clientes' as Path)}
                      class="mx-2 mb-2 flex items-center justify-center gap-1 rounded-lg py-1.5 text-xs font-medium text-foreground-subtle hover:bg-surface-muted hover:text-primary"
                    >
                      <Icon icon="material-symbols-light:add" size={16} />
                      Agregar cliente
                    </button>
                  </Show>
                </div>
              )}
            </For>

            {/* Sólo aparece si alguien quedó con un estado fuera de la configuración. */}
            <Show when={huerfanos().length > 0}>
              <div class="w-[85vw] shrink-0 sm:w-auto sm:min-w-60 sm:max-w-96 sm:flex-1 snap-start flex flex-col min-h-0 rounded-xl border border-dashed border-warning/50 bg-warning-soft/10">
                <div class="px-3 py-2 border-b border-border">
                  <div class="flex items-center justify-between gap-2">
                    <p class="font-semibold text-foreground text-sm">Sin etapa</p>
                    <span class="text-xs font-medium text-foreground-subtle bg-surface rounded-full px-2 py-0.5">{huerfanos().length}</span>
                  </div>
                  <p class="text-xs text-foreground-subtle mt-0.5">Su estado ya no existe</p>
                </div>
                <div class="flex-1 overflow-y-auto p-2 space-y-2">
                  <For each={huerfanos()}>
                    {cliente => (
                      <div
                        draggable={can(P.customers.update)}
                        onDragStart={e => empezarArrastre(e, { kind: 'customer', id: cliente.id })}
                        onDragEnd={() => setDragging(false)}
                        onClick={() => setDetalleId(cliente.id)}
                        class="rounded-lg border border-border bg-surface p-3 cursor-pointer hover:border-primary-500/50"
                      >
                        <p class="text-sm font-semibold text-foreground truncate">{cliente.name}</p>
                        <p class="text-xs text-foreground-subtle truncate">Estado: {cliente.estadoCliente || '—'}</p>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </div>

          {/* Leyenda de etapas y total, como en el diseño. */}
          <div class="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <div class="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1">
              <For each={etapas()}>
                {etapa => (
                  <span class="flex items-center gap-1.5 text-xs text-foreground-subtle">
                    <span class={`size-2 rounded-full bg-${etapa.color ?? 'default'}`} />
                    {etapa.label}
                  </span>
                )}
              </For>
            </div>
            <div class="rounded-lg bg-surface-muted px-3 py-1.5 text-sm">
              <span class="text-foreground-subtle">Total estimado en embudo: </span>
              <span class="font-bold text-foreground">{money(totalEmbudo())}</span>
            </div>
          </div>
        </Show>
      </div>

      <TableFilters title="Filtros del tablero" isOpen={filtrosAbiertos()} onClose={() => setFiltrosAbiertos(false)} activeCount={filtrosActivos()} onClear={limpiarFiltros}>
        <Input label="Buscar" value={busqueda()} onInput={e => setBusqueda(e.currentTarget.value)} placeholder="Nombre, empresa, correo, teléfono..." startIcon="material-symbols-light:search" />

        <Select
          label="Clasificación"
          options={[{ value: '', title: 'Cualquiera' }, ...classificationOptions(settings()?.customerClassifications ?? [])]}
          value={filtroClasificacion()}
          onChange={valor => setFiltroClasificacion(String(valor ?? ''))}
        />

        <Select
          label="Dado de alta"
          options={[
            { value: '', title: 'Cualquier fecha' },
            { value: 'mes', title: 'Este mes' },
            { value: 'trimestre', title: 'Último trimestre' },
            { value: 'anio', title: 'Este año' }
          ]}
          value={filtroPeriodo()}
          onChange={valor => setFiltroPeriodo(String(valor ?? ''))}
        />

        <Switch label="Sólo con importe cotizado" checked={soloConImporte()} onChange={() => setSoloConImporte(v => !v)} />
      </TableFilters>

      <CrmCustomerDrawer customerId={detalleId()} onClose={() => setDetalleId(null)} onChanged={() => void refetch()} />
      <alert.AlertComponent />
    </>
  )
}

export default CrmBoardPage
